import * as path from 'node:path';
import type { SimpleGit } from 'simple-git';
import { cliSimpleGit } from '../cli-simple-git.js';
import { parseLogShaDateSubjectLines } from './lib/parse-log-lines.js';
import { resolveBaseShaForUnpushedCommits, revParseSafe } from './remote-tracking/index.js';

const UNPUSHED_CACHE_TTL_MS = 30_000;
const unpushedCache = new Map<string, { commits: UnpushedCommitRef[]; expiresAt: number }>();

async function gitLogNotReachableFromBase(
  g: SimpleGit,
  baseSha: string,
  headSha: string,
): Promise<UnpushedCommitRef[]> {
  if (baseSha === headSha) return [];
  try {
    const countOut = await g.raw(['rev-list', '--count', `${baseSha}..${headSha}`]);
    const count = parseInt(String(countOut).trim(), 10);
    if (!Number.isFinite(count) || count <= 0) return [];
    /** Newest first (default `git log` order). */
    const logOut = await g.raw(['log', '--format=%H\t%cI\t%s', `${baseSha}..${headSha}`]);
    return parseLogShaDateSubjectLines(logOut);
  } catch {
    return [];
  }
}

/** One local commit not yet on the resolved upstream / remote default branch. */
export type UnpushedCommitRef = {
  sha: string;
  shortSha: string;
  subject: string;
  committedAt: string;
};

/**
 * Commits on HEAD not reachable from the upstream tip when set; otherwise not reachable from the
 * remote's default branch (e.g. `origin/main` via `origin/HEAD`). Newest first.
 */
export async function listUnpushedCommits(repoDir: string): Promise<UnpushedCommitRef[]> {
  const key = path.resolve(repoDir);
  const now = Date.now();
  const cached = unpushedCache.get(key);
  if (cached && cached.expiresAt > now) return cached.commits;

  const g = cliSimpleGit(repoDir);
  const [headSha, baseSha] = await Promise.all([
    revParseSafe(g, 'HEAD'),
    resolveBaseShaForUnpushedCommits(g, repoDir),
  ]);
  if (!headSha || !baseSha) {
    unpushedCache.set(key, { commits: [], expiresAt: now + UNPUSHED_CACHE_TTL_MS });
    return [];
  }
  const commits = await gitLogNotReachableFromBase(g, baseSha, headSha);
  unpushedCache.set(key, { commits, expiresAt: now + UNPUSHED_CACHE_TTL_MS });
  return commits;
}
